// Port of load_rbc_csv in scripts/loaders.py.
// RBC convention: negative = debit (money out), positive = credit (money in).
// Flip to project's positive=expense convention.
// Multi-currency via separate CAD$ / USD$ columns — exactly one is populated
// per row. Account label is the Account Type column with currency suffix
// for non-CAD rows (e.g. "RBC USD Savings (USD)").

import {
  type Transaction,
  newTransaction,
  makeId,
  parseDate,
  parseAmount,
  normalizeVendor,
  isInterPortalTransfer,
  extractLast4,
} from "../common.js";
import { parseCsv, toDictRows, findHeaderIndex } from "../csv.js";

export async function loadRbc(
  text: string,
  filename: string,
): Promise<Transaction[]> {
  const rows = parseCsv(text);
  const headerIdx = findHeaderIndex(rows, ["Transaction Date", "Description"]);
  if (headerIdx < 0) return [];
  const header = rows[headerIdx].map((h) => (h || "").trim());
  const dictRows = toDictRows(header, rows.slice(headerIdx + 1));

  const out: Transaction[] = [];
  for (const row of dictRows) {
    const rawDate = (row["Transaction Date"] || "").trim();
    if (!rawDate) continue;
    let dateStr: string;
    try {
      dateStr = parseDate(rawDate);
    } catch {
      continue;
    }
    const cad = parseAmount(row["CAD$"] || "0");
    const usd = parseAmount(row["USD$"] || "0");
    let rawAmount: number;
    let ccy: string;
    if (cad) {
      rawAmount = cad;
      ccy = "CAD";
    } else if (usd) {
      rawAmount = usd;
      ccy = "USD";
    } else {
      continue;
    }
    const amount = -rawAmount;

    const desc1 = (row["Description 1"] || "").trim();
    const desc2 = (row["Description 2"] || "").trim();
    let description = [desc1, desc2].filter(Boolean).join(" ").trim().replace(/^"|"$/g, "");
    if (!description) {
      description = (row["Account Type"] || "RBC transaction").trim();
    }
    if (isInterPortalTransfer(description)) continue;

    const acctType = (row["Account Type"] || "").trim();
    let accountLabel = acctType ? `RBC ${acctType}`.trim() : "RBC";
    if (ccy !== "CAD") accountLabel = `${accountLabel} (${ccy})`;

    // RBC's "Account Type" column is the authoritative kind signal —
    // "Visa", "Mastercard", "Credit Line" → liability; "Chequing",
    // "Savings" → debit. Unknown types leave blank.
    const acctTypeLower = acctType.toLowerCase();
    const accountKind: "liability" | "debit" | "" =
      /visa|mastercard|credit/.test(acctTypeLower) ? "liability" :
      /chequing|checking|savings/.test(acctTypeLower) ? "debit" : "";

    const id = await makeId("rbc", accountLabel, dateStr, amount, description);
    out.push(
      newTransaction({
        id,
        date: dateStr,
        vendor_raw: description,
        vendor_key: normalizeVendor(description) || "rbc",
        amount: Math.round(amount * 100) / 100,
        portal: "rbc",
        account: accountLabel,
        account_kind: accountKind,
        account_last4: extractLast4(row["Account Number"] || ""),
        source_file: filename,
        raw: { ...row },
      }),
    );
  }
  return out;
}
